/**
 * Icon + eyebrow + value.
 *
 * Lifted from the publish drawer's "what will happen" pattern and reused for
 * "what happened": the last deployment, the deploy summary, the empty-state
 * walkthrough on /apps.
 */
const TileRow = ({ tile, eyebrow, value, trailing, isFirst = false, ringed = false, wraps = false, onClick, className = '' }: {
  /** Icon, or a step number. */
  tile: React.ReactNode,
  eyebrow: React.ReactNode,
  value: React.ReactNode,
  trailing?: React.ReactNode,
  isFirst?: boolean,
  /** For a step number, which does not read as a step without one. */
  ringed?: boolean,
  /**
   * Let the value run onto a second line instead of ending in an ellipsis.
   * For a row whose value is a sentence — a summary's value is a name or a
   * setting, and one line of those is the point; a sentence cut at the width
   * of a phone is just missing.
   */
  wraps?: boolean,
  /** Makes the row a button — for a summary whose every line is also the way
   *  to the screen that sets it. */
  onClick?: () => void,
  className?: string,
}) => {
  const Row = onClick ? 'button' : 'div';

  return (
  <Row
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    className={`w-full text-left flex items-center gap-3.5 px-4 py-3.5 ${
      isFirst ? '' : 'border-t border-navy-500'
    } ${onClick ? 'b4-focus hover:bg-navy-600 transition-colors duration-[120ms]' : ''} ${className}`}
  >
    {/* The icon, not a box around the icon. The 44px tile was a fourth tint
        and a fourth border inside a card that already had three, to hold a
        glyph that carries on its own.

        The slot grew with the glyph: the stroke is 1px of screen now, so a
        bigger icon is a clearer one rather than a heavier one, and 22px in a
        24px slot was touching both edges. */}
    <div className={`flex-none flex items-center justify-center text-navy-100 font-courrier text-ds-125 ${
      ringed ? 'w-8 h-8 rounded-full border border-navy-500 bg-navy-600' : 'w-8'
    }`}>
      {tile}
    </div>
    <div className="flex-1 min-w-0 flex flex-col gap-[3px]">
      <span className="b4-eyebrow-tight">{eyebrow}</span>
      <span className={`text-ds-135 leading-[1.4] text-white ${wraps ? 'text-pretty' : 'truncate'}`}>{value}</span>
    </div>
    {trailing}
  </Row>
  );
}

export default TileRow
